<template>
    <div :class="classes([type])">
        <div
          ref="wrap"
          :style="wrapStyle"
          :class="[classes('wrap', { scrollable }), { 'hanzo-hairline--top-bottom': type === 'line' }]"
        >
          <div ref="nav" :class="classes('nav', [type])" :style="navStyle">
            <slot name="nav-left"></slot>
            <div v-if="type === 'line' || type === 'menu'" :class="classes('line')" :style="lineStyle" />
            <div
                v-for="(tab,index) in tabs"
                :key="index"
                ref="tabs"
                refInFor
                :class="classes('item',{active: index === curActive,disabled: tab.disabled,complete: !ellipsis})"
                :style="getTabStyle(tab, index)"
                @click="onClick(index)"
            >
                <span ref="title" refInFor :class="{ 'hanzo-ellipsis': ellipsis }">{{ tab.title }}</span>
            </div>
            <slot name="nav-right"></slot>
          </div>
        </div>
        <div ref="content" :class="classes('content', { animated, menu: type === 'menu' })">
            <div v-if="animated" :class="classes('track')" :style="trackStyle">
              <slot></slot>
            </div>
            <slot v-else></slot>
        </div>
      </div>
</template>
<script>
import { classesMixin } from '../../../common/mixins/classname';
import { TouchMixin } from '../../../common/mixins/touch';
import { isDef } from '../../../common/utils/index';
import { on, off } from '../../../common/utils/event';
import { raf } from '../../../common/utils/raf';
import { setScrollTop, getScrollTop, getElementTop, getScrollEventTarget } from '../../../common/utils/scroll';
let classMixin = classesMixin('menu');
export default {
    mixins: [classMixin,TouchMixin],
    name: "h-menu",

    model: {
        prop: 'active'
    },

    props: {
        color: String,
        sticky: Boolean,
        animated: Boolean,
        offsetTop: Number,
        swipeable: Boolean,
        background: String,
        titleActiveColor: String,
        titleInactiveColor: String,
        ellipsis: {
            type: Boolean,
            default: true
        },
        lazyRender: {
            type: Boolean,
            default: true
        },
        lineWidth: {
            type: Number,
            default: null
        },
        lineHeight: {
            type: Number,
            default: null
        },
        active: {
            type: [Number, String],
            default: 0
        },
        type: {
            type: String,
            default: 'line'
        },
        duration: {
            type: Number,
            default: 0.3
        },
        swipeThreshold: {
            type: Number,
            default: 4
        }
    },

    data() {
        return {
            tabs: [],
            position: '',
            curActive: null,
            lineStyle: {
                backgroundColor: this.color
            },
            events: {
                resize: false,
                sticky: false,
                swipeable: false
            }
        };
    },

    computed: {
        // whether the nav is scrollable
        scrollable() {
            return this.tabs.length > this.swipeThreshold || !this.ellipsis;
        },

        wrapStyle() {
            switch (this.position) {
                case 'top':
                return {
                    top: this.offsetTop + 'px',
                    position: 'fixed'
                };
                case 'bottom':
                return {
                    top: 'auto',
                    bottom: 0
                };
                default:
                return null;
            }
        },

        navStyle() {
            return {
                borderColor: this.color,
                background: this.background
            };
        },

        trackStyle() {
            if (this.animated) {
                return {
                left: `${-1 * this.curActive * 100}%`,
                transitionDuration: `${this.duration}s`
                };
            }
        }
    },

    watch: {
        active(val) {
            if (val !== this.curActive) {
                this.correctActive(val);
            }
        },

        color() {
            this.setLine();
        },

        tabs() {
            this.correctActive(this.curActive || this.active);
            // this.scrollIntoView(); 
            this.onShow();
            this.setLine();
        },

        curActive() {
            this.scrollIntoView();
            this.setLine();

            // scroll to correct position
            if (this.position === 'top' || this.position === 'bottom') {
                setScrollTop(window, getElementTop(this.$el) - this.offsetTop);
            }
        },

        sticky() {
            this.handlers(true);
        },

        swipeable() {
            this.handlers(true);
        }
    },

    mounted() {
        this.onShow();
    },

    activated() {
        this.onShow();
        this.setLine();
    },

    deactivated() {
        this.handlers(false);
    },

    beforeDestroy() {
        this.handlers(false);
    },

    methods: {
        onShow() {
            this.$nextTick(() => {
                this.inited = true;
                this.handlers(true);
                if ( this.type !== 'menu' ) {
                    this.scrollIntoView(true);
                } else {
                    this.scrollIntoView();
                }
            });
        },

        // whether to bind sticky listener
        handlers(bind) {
            const { events } = this;
            const sticky = this.sticky && bind;
            const swipeable = this.swipeable && bind;

            // listen to window resize event
            if (events.resize !== bind) {
                events.resize = bind;
                (bind ? on : off)(window, 'resize', this.setLine, true);
            }

            // listen to scroll event
            if (events.sticky !== sticky) {
                events.sticky = sticky;
                this.scrollEl = this.scrollEl || getScrollEventTarget(this.$el);
                (sticky ? on : off)(this.scrollEl, 'scroll', this.onScroll, true);
                this.onScroll();
            }

            // listen to touch event
            if (events.swipeable !== swipeable) {
                events.swipeable = swipeable;
                const { content } = this.$refs;
                const action = swipeable ? on : off;

                action(content, 'touchstart', this.touchStart);
                action(content, 'touchmove', this.touchMove);
                action(content, 'touchend', this.onTouchEnd);
                action(content, 'touchcancel', this.onTouchEnd);
            }
        },

        // watch swipe touch end
        onTouchEnd() {
            const { direction, deltaX, curActive } = this;
            console.log('data', this.data);
            const minSwipeDistance = 50;

            /* istanbul ignore else */
            if (direction === 'horizontal' && this.offsetX >= minSwipeDistance) {
                /* istanbul ignore else */
                if (deltaX > 0 && curActive !== 0) {
                    this.setCurActive(curActive - 1);
                } else if (deltaX < 0 && curActive !== this.tabs.length - 1) {
                    this.setCurActive(curActive + 1);
                }
            }
        },

        // adjust tab position
        onScroll() {
            const scrollTop = getScrollTop(window) + this.offsetTop;
            const elTopToPageTop = getElementTop(this.$el);
            const elBottomToPageTop =
                elTopToPageTop + this.$el.offsetHeight - this.$refs.wrap.offsetHeight;
            if (scrollTop > elBottomToPageTop) {
                this.position = 'bottom';
            } else if (scrollTop > elTopToPageTop) {
                this.position = 'top';
            } else {
                this.position = '';
            }
            const scrollParams = {
                scrollTop,
                isFixed: this.position === 'top'
            };
            this.$emit('scroll', scrollParams);
        },

        // update nav bar style
        setLine() {
            const shouldAnimate = this.inited;

            this.$nextTick(() => {
                const { tabs } = this.$refs;

                if (!tabs || !tabs[this.curActive]) {
                    return;
                }

                if ( this.type === 'line' ) {
                    const tab = tabs[this.curActive];
                    const { lineWidth, lineHeight } = this;
                    // const width = isDef(lineWidth) ? lineWidth : tab.offsetWidth / 2;
                    const width = 25;
                    const left = tab.offsetLeft + (tab.offsetWidth - width) / 2;

                    const lineStyle = {
                        width: `${width}px`,
                        backgroundColor: this.color,
                        transform: `translateX(${left}px)`
                    };

                    if (shouldAnimate) {
                        lineStyle.transitionDuration = `${this.duration}s`;
                    }

                    if (isDef(lineHeight)) {
                        const height = `${lineHeight}px`;
                        lineStyle.height = height;
                        lineStyle.borderRadius = height;
                    }

                    this.lineStyle = lineStyle;
                } else if ( this.type === 'menu' ) {
                    const tab = tabs[this.curActive];
                    const { lineWidth, lineHeight } = this;
                    const height = 15;
                    const top = tab.offsetTop + (tab.offsetHeight - height) / 2;

                    const lineStyle = {
                        backgroundColor: this.color,
                        transform: `translateY(${top}px)`
                    };

                    if (shouldAnimate) {
                        lineStyle.transitionDuration = `${this.duration}s`;
                    }

                    // if (isDef(lineHeight)) {
                    //     const height = `${lineHeight}px`;
                    //     lineStyle.height = height;
                    //     lineStyle.borderRadius = height;
                    // }

                    this.lineStyle = lineStyle;
                }

                
            });
        },

        // correct the value of active
        correctActive(active) {
            active = +active;
            const exist = this.tabs.some(tab => tab.index === active);
            const defaultActive = (this.tabs[0] || {}).index || 0;
            this.setCurActive(exist ? active : defaultActive);
        },

        setCurActive(active) {
            active = this.findAvailableTab(active, active < this.curActive);
            if (isDef(active) && active !== this.curActive) {
                this.$emit('input', active);

                if (this.curActive !== null) {
                    this.$emit('change', active, this.tabs[active].title);
                }
                this.curActive = active;
            }
        },

        findAvailableTab(active, reverse) {
            const diff = reverse ? -1 : 1;
            let index = active;

            while (index >= 0 && index < this.tabs.length) {
                if (!this.tabs[index].disabled) {
                    return index;
                }
                index += diff;
            }
        },

        // emit event when clicked
        onClick(index) {
            const { title, disabled, data } = this.tabs[index];
            if (disabled) {
                this.$emit('disabled', index, title);
            } else {
                this.setCurActive(index);
                this.$emit('click', index, title, data);
            }
        },

        // scroll active tab into view
        scrollIntoView(immediate) {
            const { tabs } = this.$refs;

            if (!this.scrollable || !tabs || !tabs[this.curActive]) {
                return;
            }
            
            if ( this.type !== 'menu' ) {
                const { nav } = this.$refs;
                const { scrollLeft, offsetWidth: navWidth } = nav;
                const { offsetLeft, offsetWidth: tabWidth } = tabs[this.curActive];
                
                this.scrollTo(nav, scrollLeft, offsetLeft - (navWidth - tabWidth) / 2, immediate);
            } else {
                const { nav } = this.$refs;
                const { scrollTop, offsetHeight: navHeight } = nav;
                const { offsetTop, offsetHeight: tabHeight } = tabs[this.curActive];
                
                this.scrollTo(nav, scrollTop, offsetTop - (navHeight - tabHeight) / 2, immediate);
            }    
        },

        // animate the scrollLeft of nav
        scrollTo(el, from, to, immediate) {
            if (immediate) {
                el.scrollLeft += to - from;
                return;
            }

            let count = 0;
            const frames = Math.round((this.duration * 1000) / 16);
            const animate = () => {
                if ( this.type !== 'menu' ) {
                    el.scrollLeft += (to - from) / frames;
                    /* istanbul ignore next */
                    if (++count < frames) {
                        raf(animate);
                    }
                } else {
                    el.scrollTop += (to - from) / frames;
                    /* istanbul ignore next */
                    if (++count < frames) {
                        raf(animate);
                    }
                }
                
            };
            animate();
        },

        // render title slot of child tab
        renderTitle(el, index) {
            this.$nextTick(() => {
                const title = this.$refs.title[index];
                title.parentNode.replaceChild(el, title);
            });
        },

        getTabStyle(item, index) {
            const style = {};
            const { color } = this;
            const active = index === this.curActive;
            const isCard = this.type === 'card';

            // theme color
            if (color) {
                if (!item.disabled && isCard && !active) {
                    style.color = color;
                }
                if (!item.disabled && isCard && active) {
                    style.backgroundColor = color;
                }
                if (isCard) {
                    style.borderColor = color;
                }
            }

            const titleColor = active ? this.titleActiveColor : this.titleInactiveColor;
            if (titleColor) {
                style.color = titleColor;
            }

            if (this.scrollable && this.ellipsis) {
                style.flexBasis = 88 / this.swipeThreshold + '%';
            }

            return style;
        }
    }
}
</script>
<style lang="less" scoped>
@import './index.less';
</style>